import { useClerk } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SaveMyProfileApiPayload } from '@/actions/my-profile/shared';
import DeleteAccountDialog from '@/components/dialogs/DeleteAccountDialog';
import { MyProfileForm } from '@/components/forms/profile/MyProfileForm';
import { ResetPasswordForm } from '@/components/forms/profile/ResetPasswordForm';
import { MyProfileFormSchema } from '@/components/forms/schemas/MyProfileFormSchema';
import { Button } from '@/components/ui/button';
import { useCreateMyProfile } from '@/hooks/my-profile/useCreateMyProfile';
import { useGetMyProfile } from '@/hooks/my-profile/useGetMyProfile';
import { useUpdateMyProfile } from '@/hooks/my-profile/useUpdateMyProfile';
import { toast } from '@/hooks/ui/use-toast';
import { useDeleteUser } from '@/hooks/user/useDeleteUser';
import useMyProfileStore from '@/stores/useMyProfileStore';
import { MyProfile } from '@/types/my-profile.type';

type MyProfileFormValues = z.infer<typeof MyProfileFormSchema>;

interface MyProfileTabProps {
  setIsDirty: (isDirty: boolean) => void;
}

export const MyProfileTab = ({ setIsDirty }: MyProfileTabProps) => {
  const { myProfile, setMyProfile } = useMyProfileStore();

  const myProfileForm = useForm<MyProfileFormValues>({
    resolver: zodResolver(MyProfileFormSchema),
    defaultValues: {
      photo_url: '',
      first_name: '',
      last_name: '',
      consultant_firm_affiliated: [],
      phone: '',
      email: '',
      city: null,
      state: null,
      zip_code: null,
      address: null,
      meeting_link: null,
      linkedin: null,
      website: null,
      preferred_markets: [],
    },
  });
  const { signOut } = useClerk();

  const {
    result: myProfileResult,
    isError: isMyProfileError,
    isSuccess: isMyProfileSuccess,
    isLoading: isMyProfileLoading,
    isRefetching: isMyProfileRefetching,
  } = useGetMyProfile();
  const {
    mutate: createMyProfile,
    result: createMyProfileResult,
    isError: isCreateMyProfileError,
    isSuccess: isCreateMyProfileSuccess,
    isLoading: isCreateMyProfileLoading,
  } = useCreateMyProfile();
  const {
    mutate: updateMyProfile,
    result: updateMyProfileResult,
    isError: isUpdateMyProfileError,
    isSuccess: isUpdateMyProfileSuccess,
    isLoading: isUpdateMyProfileLoading,
  } = useUpdateMyProfile();
  const { mutate: deleteUser } = useDeleteUser();

  const resetForms = (profile?: MyProfile) => {
    myProfileForm.reset({
      photo_url: profile?.photo_url || '',
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      consultant_firm_affiliated: profile?.consultant_firm_affiliated || [],
      phone: profile?.phone || '',
      email: profile?.email || '',
      city: profile?.city || null,
      state: profile?.state || null,
      zip_code: profile?.zip_code || null,
      address: profile?.address || null,
      meeting_link: profile?.meeting_link || null,
      linkedin: profile?.linkedin || null,
      website: profile?.website || null,
      preferred_markets: profile?.preferred_markets || [],
    });
    setIsDirty(false);
  };

  const handleSubmit = async (): Promise<void> => {
    const isMyProfileFormValid = await myProfileForm.trigger();
    if (!isMyProfileFormValid) {
      return;
    }

    const formData = myProfileForm.getValues();
    const payload: SaveMyProfileApiPayload = {
      photo_url: formData.photo_url || null,
      first_name: formData.first_name,
      last_name: formData.last_name,
      consultant_firm_affiliated: formData.consultant_firm_affiliated ?? [],
      phone: formData.phone,
      email: formData.email,
      city: formData.city || null,
      state: formData.state || null,
      zip_code: formData.zip_code || null,
      address: formData.address || null,
      meeting_link: formData.meeting_link || null,
      linkedin: formData.linkedin || null,
      website: formData.website || null,
      preferred_markets: formData.preferred_markets ?? [],
    };

    if (myProfile) {
      await updateMyProfile(payload);
    } else {
      await createMyProfile(payload);
    }
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async (): Promise<void> => {
    if (!myProfile?.id) {
      toast({
        title: 'Something went wrong',
        description: 'Profile is not available yet.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsDeleting(true);
      await deleteUser(myProfile.id);
      toast({ description: 'Account deleted successfully. Signing out…' });
      setTimeout(() => {
        signOut({ redirectUrl: '/' });
      }, 800);
    } catch {
      toast({ title: 'Something went wrong', description: 'Please try again' });
    } finally {
      setIsDeleting(false);
    }
  };

  // render functions
  const renderDeleteAccountSection = () => (
    <div className="flex flex-col flex-wrap items-center justify-between gap-6 rounded-xl border border-border bg-white p-6 md:flex-row">
      <div className="flex gap-2">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-red-100 p-2">
          <TriangleAlert color="red" className="h-4 w-4" />
        </div>
        <div className="as-title-03 as-strong text-zinc-700">
          Delete account
          <p className="as-body-04">
            Deleting this account is permanent and cannot be undone. Please
            contact the support team to recover your account.
          </p>
        </div>
      </div>
      <Button
        className="w-full bg-red-100 text-pink-strong md:w-fit"
        onClick={() => setIsDeleteDialogOpen(true)}
      >
        Delete account
      </Button>
    </div>
  );

  const renderEditButtons = () => {
    return (
      <div className="flex w-full justify-end">
        <div className="flex gap-1">
          <Button size="sm" className="w-full md:w-fit" onClick={handleSubmit}>
            Save Change
          </Button>
        </div>
      </div>
    );
  };

  // effects
  useEffect(() => {
    setIsDirty(myProfileForm.formState.isDirty);
  }, [myProfileForm.formState.isDirty, setIsDirty]);

  useEffect(() => {
    if (isMyProfileError || isCreateMyProfileError || isUpdateMyProfileError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isMyProfileError, isCreateMyProfileError, isUpdateMyProfileError]);

  useEffect(() => {
    if (isMyProfileSuccess && myProfileResult?.data) {
      setMyProfile({ ...myProfileResult.data });
      resetForms(myProfileResult.data);
    }
  }, [myProfileResult, isMyProfileSuccess, setMyProfile]);

  useEffect(() => {
    if (!isCreateMyProfileSuccess && !isUpdateMyProfileSuccess) {
      return;
    }

    const newData = createMyProfileResult?.data || updateMyProfileResult?.data;
    if (newData) {
      setMyProfile({ ...newData });
      resetForms(newData);
      toast({ description: 'Changes saved successfully' });
    }
  }, [
    createMyProfileResult,
    updateMyProfileResult,
    isCreateMyProfileSuccess,
    isUpdateMyProfileSuccess,
    setMyProfile,
  ]);

  // no parent auto-save registration; manual save only

  if (isMyProfileLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  const isLoading =
    isCreateMyProfileLoading ||
    isUpdateMyProfileLoading ||
    isMyProfileRefetching;

  return (
    <div className="relative space-y-1.5">
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <MyProfileForm form={myProfileForm} />
      <ResetPasswordForm />
      {renderDeleteAccountSection()}
      {renderEditButtons()}
      <DeleteAccountDialog
        open={isDeleteDialogOpen}
        loading={isDeleting}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
};
